import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { membersAPI, eventsAPI, unwrapList } from '../../services/api';
import { LoadingPage } from '../../components/Loading';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { CATEGORY_LABELS, membershipStatus } from '../../utils/membership';
import './Pos.css';

const statusLabel = {
  confirmed: '已確認',
  pending: '待確認',
  cancelled: '已取消',
  present: '已出席',
  absent: '缺席',
};

const MemberCard = () => {
  const { id } = useParams();
  const { success, error } = useToast();
  const { user } = useAuth();
  const volunteer = user?.desk_role === 'volunteer';
  const [member, setMember] = useState(null);
  const [profile, setProfile] = useState({
    chinese_name: '',
    english_name: '',
    phone_number: '',
    email: '',
    district: '',
    is_active: true,
    is_verified: false,
    id_card_number: '',
    notes: '',
    member_category: 'regular',
    membership_expires_on: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState('');
  const [actionMessage, setActionMessage] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await membersAPI.getMember(id);
        if (!cancelled) {
          const data = response.data;
          setMember(data);
          setProfile({
            chinese_name: data.chinese_name || '',
            english_name: data.english_name || '',
            phone_number: data.phone_number || '',
            email: data.email || '',
            district: data.district || '',
            is_active: Boolean(data.is_active),
            is_verified: Boolean(data.is_verified),
            id_card_number: data.id_card_number || '',
            notes: data.notes || '',
            member_category: data.member_category || 'regular',
            membership_expires_on: data.membership_expires_on || '',
          });
        }
      } catch (err) {
        if (!cancelled) {
          error(err.response?.data?.error || '找不到會員');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    eventsAPI.adminList()
      .then((response) => {
        const rows = unwrapList(response.data);
        setEvents(rows);
        if (rows[0]) setEventId(String(rows[0].id));
      })
      .catch(() => setEvents([]));
  }, []);

  const refreshMember = async () => {
    const refreshed = await membersAPI.getMember(id);
    setMember(refreshed.data);
  };

  const register = async () => {
    if (!eventId) return;
    setActionMessage('');
    try {
      const response = await eventsAPI.registerMember(eventId, id);
      setActionMessage(response.data.message || '報名完成');
      await refreshMember();
    } catch (err) {
      setActionMessage(err.response?.data?.message || err.response?.data?.error || '報名失敗');
    }
  };

  const removeRow = async (kind, rowId) => {
    if (!window.confirm('確認刪除？')) return;
    setActionMessage('');
    try {
      if (kind === 'registration') await eventsAPI.deleteRegistration(rowId);
      else await eventsAPI.deleteAttendance(rowId);
      setActionMessage(kind === 'registration' ? '已刪除報名' : '已刪除簽到');
      await refreshMember();
    } catch (err) {
      setActionMessage(err.response?.data?.error || '刪除失敗');
    }
  };

  const saveAdvanced = async () => {
    setSaving(true);
    try {
      const response = await membersAPI.patch(id, {
        is_verified: profile.is_verified,
        id_card_number: profile.id_card_number,
      });
      setMember(response.data);
      success('會員進階資料已儲存');
    } catch (err) {
      error('會員進階資料儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const regenerateQr = async () => {
    if (!window.confirm('確認重發 QR 令牌？舊碼將不能再用。')) return;
    try {
      const response = await membersAPI.regenerateQr(id);
      setMember(response.data);
      success('QR 令牌已更新');
    } catch (err) {
      error('QR 令牌更新失敗');
    }
  };

  const checkIn = async () => {
    if (!eventId || !member?.member_number) return;
    setActionMessage('');
    try {
      const response = await eventsAPI.checkIn({
        q: member.member_number,
        event_id: eventId,
        register_and_check_in: false,
      });
      setActionMessage(response.data.message || '簽到完成');
      await refreshMember();
    } catch (err) {
      setActionMessage(err.response?.data?.message || err.response?.data?.error || '簽到失敗');
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const response = await membersAPI.patch(id, {
        ...profile,
        membership_expires_on: profile.membership_expires_on || null,
      });
      setMember(response.data);
      success('資料已儲存');
    } catch (err) {
      const data = err.response?.data;
      error(data?.phone_number?.[0] || data?.phone_number || '資料儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const setField = (name, value) => setProfile((current) => ({ ...current, [name]: value }));

  if (loading) return <LoadingPage message="載入會員卡..." />;
  if (!member) return <div className="pos-error">找不到會員</div>;

  const active = member.is_active;

  return (
    <article className="pos-card">
      <p>
        <Link to="/admin/members">返回會員</Link>
        {' · '}
        <Link to={`/admin/members/${id}/print`}>列印會員卡／貼紙</Link>
      </p>
      <div className="pos-card-head">
        <div>
          <h1>{member.chinese_name}</h1>
          <div className={`pos-status ${active ? 'green' : 'red'}`}>
            {active ? '有效' : '已停用'}
          </div>
        </div>
        {member.qr_code_token && (
          <div className="pos-member-qr" aria-label="會員 QR 碼">
            <QRCodeSVG
              value={member.qr_code_token}
              size={168}
              level="H"
              includeMargin
            />
          </div>
        )}
      </div>
      <div className="pos-meta">
        <div>會員編號：{member.member_number}</div>
        {!volunteer && <div>電話：{member.phone_number || '—'}</div>}
        {!volunteer && <div>電郵：{member.email || '—'}</div>}
        <div>地區：{member.district || '—'}</div>
        <div>類別：{CATEGORY_LABELS[member.member_category] || '普通會員'}</div>
        <div>會籍：{membershipStatus(member.membership_expires_on).label}{member.membership_expires_on ? `（${member.membership_expires_on}）` : ''}</div>
        <div>驗證：{member.is_verified ? '已驗證' : '未驗證'}</div>
      </div>

      {!volunteer && (
        <section className="pos-section">
          <h2>編輯資料</h2>
          <div className="pos-form-grid">
            <label>中文姓名
              <input value={profile.chinese_name} onChange={(e) => setField('chinese_name', e.target.value)} />
            </label>
            <label>英文姓名
              <input value={profile.english_name} onChange={(e) => setField('english_name', e.target.value)} />
            </label>
            <label>電話
              <input value={profile.phone_number} onChange={(e) => setField('phone_number', e.target.value)} />
            </label>
            <label>電郵
              <input value={profile.email} onChange={(e) => setField('email', e.target.value)} />
            </label>
            <label>地區
              <input value={profile.district} onChange={(e) => setField('district', e.target.value)} />
            </label>
            <label>會員類別
              <select value={profile.member_category || 'regular'} onChange={(e) => setField('member_category', e.target.value)}>
                {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
            <label>會籍到期日（留空表示無到期）
              <input type="date" value={profile.membership_expires_on || ''} onChange={(e) => setField('membership_expires_on', e.target.value)} />
            </label>
            <label className="pos-check">
              <input
                type="checkbox"
                checked={profile.is_active}
                onChange={(e) => setField('is_active', e.target.checked)}
              />
              帳戶有效
            </label>
            <label>職員備註
              <textarea className="pos-notes" value={profile.notes} onChange={(e) => setField('notes', e.target.value)} />
            </label>
          </div>
          <div className="pos-actions">
            <button type="button" className="pos-btn" onClick={saveProfile} disabled={saving}>
              {saving ? '儲存中...' : '儲存資料'}
            </button>
          </div>
        </section>
      )}

      {!volunteer && (
        <section className="pos-section">
          <h2>會員進階</h2>
          <label className="pos-check">
            <input type="checkbox" checked={profile.is_verified} onChange={(e) => setField('is_verified', e.target.checked)} />
            已驗證
          </label>
          <label>身份證號碼
            <input value={profile.id_card_number} onChange={(e) => setField('id_card_number', e.target.value)} />
          </label>
          <div>QR 令牌：{member.qr_code_token || '—'}</div>
          <div className="pos-actions">
            <button type="button" className="pos-btn" onClick={saveAdvanced} disabled={saving}>儲存進階資料</button>
            <button type="button" className="pos-btn secondary" onClick={regenerateQr}>重發 QR 令牌</button>
          </div>
        </section>
      )}

      <section className="pos-section">
        <h2>報名活動／簽到活動</h2>
        {events.length === 0 ? (
          <p>暫無活動</p>
        ) : (
          <>
            <select className="pos-station-select" value={eventId} onChange={(e) => setEventId(e.target.value)}>
              {events.map((event) => (
                <option key={event.id} value={event.id}>{event.title}</option>
              ))}
            </select>
            <div className="pos-actions">
              <button type="button" className="pos-btn" onClick={register}>報名活動</button>
              <button type="button" className="pos-btn secondary" onClick={checkIn}>簽到活動</button>
            </div>
            {actionMessage && <p className="pos-hint">{actionMessage}</p>}
          </>
        )}
      </section>

      <section className="pos-section">
        <h2>活動記錄</h2>
        {member.registrations?.length || member.attendances?.length ? (
          <ul className="pos-list">
            {(member.registrations || []).map((row) => (
              <li key={`reg-${row.id}`}>
                報名 · {row.event_title} · {statusLabel[row.status] || row.status}
                {!volunteer && (
                  <button type="button" className="pos-btn secondary" onClick={() => removeRow('registration', row.id)}>刪除報名</button>
                )}
              </li>
            ))}
            {(member.attendances || []).map((row) => (
              <li key={`att-${row.id}`}>
                簽到 · {row.event_title} · {statusLabel[row.status] || row.status}
                {!volunteer && (
                  <button type="button" className="pos-btn secondary" onClick={() => removeRow('attendance', row.id)}>刪除簽到</button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p>暫無活動記錄</p>
        )}
      </section>
    </article>
  );
};

export default MemberCard;
